import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { TeamMember } from '@/features/iam/teams/TeamMember'
import { type TeamMemberStore } from '@/features/iam/teams/TeamMemberStore'
import type { AlertErrorHandlerFactory } from '@/features/shell'

export class RemoveTeamMemberDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable accessor teamMember = TeamMember.none

  constructor(
    private readonly _teamMemberStore: TeamMemberStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Asks to confirm removing the team member.
   */
  @action.bound
  show(teamMember: TeamMember) {
    this.teamMember = teamMember
    this.modal.show()
  }

  /**
   * Confirms the removal, ignoring repeated confirms while it is in flight.
   */
  @action.bound
  confirm() {
    if (!this.remove.pending) {
      this.remove().catch(() => undefined)
    }
  }

  /**
   * Removes the team member. The dialog closes either way; a failure is
   * alerted.
   */
  readonly remove = task.resolved(async () => {
    try {
      await this._teamMemberStore
        .deleteTeamMember({
          teamId: this.teamMember.team.id,
          userPublicId: this.teamMember.member.user.publicId,
        })
        .catch(this._createAlertErrorHandler('Removing team member failed'))
    } finally {
      this.modal.close()
    }
  })
}
